// Sugalarm — /api/auth wrappers.

import { request } from "../lib/apiClient";
import type { ApiResult, AuthTokens, User } from "../lib/types";

export interface LoginIn {
  email: string;
  password: string;
}
export interface RegisterIn {
  email: string;
  password: string;
  display_name: string;
}

export interface AuthResponse {
  user: User;
  tokens: AuthTokens;
}

export function login(body: LoginIn): Promise<ApiResult<AuthResponse>> {
  return request<AuthResponse>("/api/auth/login", {
    method: "POST",
    body,
    noAuth: true,
  });
}

export function register(body: RegisterIn): Promise<ApiResult<AuthResponse>> {
  return request<AuthResponse>("/api/auth/register", {
    method: "POST",
    body,
    noAuth: true,
  });
}

export function me(): Promise<ApiResult<User>> {
  return request<User>("/api/auth/me");
}

/**
 * Cycle 419 — this call is what REVOKES the session now, so it has to reach
 * the server holding a valid credential.
 *
 * ⚠ `bearer` IS NOT OPTIONAL POLISH. `teardownAccount` fires the network
 * work without awaiting it and then runs steps 2-6, the last of which is
 * `clearAll()`. The logout POST is issued AFTER a release round-trip and a
 * `disablePush()` — i.e. hundreds of milliseconds later — by which time
 * `getBearer()` returns null and the request goes out UNAUTHENTICATED. It
 * would 401, revoke nothing, and (worse) drive `request()`'s 401 dance into
 * a second teardown. The caller captures the access token before any of that
 * and hands it in here.
 *
 * `noRetry` for the same reason: there is nothing to refresh onto, and the
 * 401 dance on a sign-out is noise at best and recursion at worst.
 */
export function logout(bearer?: string | null): Promise<ApiResult<void>> {
  return request<void>("/api/auth/logout", {
    method: "POST",
    ...(bearer ? { bearer, noRetry: true } : {}),
  });
}

// Cycle 95: public single-use password-reset consume (unauthenticated).
export interface ResetPasswordIn {
  token: string;
  new_password: string;
}

export function resetPassword(
  body: ResetPasswordIn,
): Promise<ApiResult<{ message: string }>> {
  return request<{ message: string }>("/api/auth/reset_password", {
    method: "POST",
    body,
    noAuth: true,
    noRetry: true,
  });
}

// Cycle 190: public self-serve reset REQUEST. On a 200 the caller shows a
// generic confirmation regardless of whether the account exists
// (anti-enumeration — the server never varies status/body by account state).
export function requestPasswordReset(
  email: string,
): Promise<ApiResult<{ message: string }>> {
  return request<{ message: string }>("/api/auth/forgot_password", {
    method: "POST",
    body: { email },
    noAuth: true,
    noRetry: true,
  });
}

// ─── Cycle 152: multi-method sign-in ─────────────────────────────────────
// Every flow returns the SAME AuthResponse the password login returns —
// the caller hands it to useAuth.adoptSession (one session path).

export interface ProvidersOut {
  google: boolean;
  apple: boolean;
  passkey: boolean;
  google_client_id: string | null;
  apple_client_id: string | null;
  rp_id: string | null;
}

export function providers(): Promise<ApiResult<ProvidersOut>> {
  return request<ProvidersOut>("/api/auth/providers", { noAuth: true });
}

export function oauthGoogle(
  idToken: string,
  displayName?: string,
): Promise<ApiResult<AuthResponse>> {
  return request<AuthResponse>("/api/auth/oauth/google", {
    method: "POST",
    body: { id_token: idToken, display_name: displayName ?? null },
    noAuth: true,
  });
}

export function oauthApple(
  identityToken: string,
  displayName?: string,
): Promise<ApiResult<AuthResponse>> {
  return request<AuthResponse>("/api/auth/oauth/apple", {
    method: "POST",
    body: { identity_token: identityToken, display_name: displayName ?? null },
    noAuth: true,
  });
}

export interface PasskeyOptionsOut {
  challenge_id: string;
  options: Record<string, unknown>;
}

export interface PasskeyCredentialOut {
  id: string;
  label: string | null;
  created_at: string;
  last_used_at: string | null;
}

export function passkeyRegisterBegin(): Promise<ApiResult<PasskeyOptionsOut>> {
  return request<PasskeyOptionsOut>("/api/auth/passkey/register/begin", {
    method: "POST",
    body: {},
  });
}

export function passkeyRegisterFinish(
  challengeId: string,
  credential: Record<string, unknown>,
  label?: string,
): Promise<ApiResult<PasskeyCredentialOut>> {
  return request<PasskeyCredentialOut>("/api/auth/passkey/register/finish", {
    method: "POST",
    body: { challenge_id: challengeId, credential, label: label ?? null },
  });
}

export function passkeyAuthBegin(
  email?: string,
): Promise<ApiResult<PasskeyOptionsOut>> {
  return request<PasskeyOptionsOut>("/api/auth/passkey/authenticate/begin", {
    method: "POST",
    body: { email: email ?? null },
    noAuth: true,
  });
}

export function passkeyAuthFinish(
  challengeId: string,
  credential: Record<string, unknown>,
): Promise<ApiResult<AuthResponse>> {
  return request<AuthResponse>("/api/auth/passkey/authenticate/finish", {
    method: "POST",
    body: { challenge_id: challengeId, credential },
    noAuth: true,
  });
}

export function passkeyCredentials(): Promise<
  ApiResult<PasskeyCredentialOut[]>
> {
  return request<PasskeyCredentialOut[]>("/api/auth/passkey/credentials");
}

export function deletePasskeyCredential(
  id: string,
): Promise<ApiResult<{ message: string }>> {
  return request<{ message: string }>(
    `/api/auth/passkey/credentials/${id}`,
    { method: "DELETE" },
  );
}
